import {Component, OnInit} from "@angular/core";
import {FormBuilder, FormGroup, Validators} from "@angular/forms";
import {AlertifyService} from "../api-service/alertify.service";
import {UsuarioPerfilResponse} from "../core/response/usuario-perfil/usuario-perfil-response";
import {EventEmitterBaseService} from "../core/services/event-emitter-service/event-emitter.service";
import {UserCredentials} from "../core/types/user-credentials";
import {AuthService} from "../services/auth.service";
import {CognitoService} from "../core/services/cognito/cognito.service";
import {SessionService} from "../core/services/session/session.service";
import {AppEvents} from "../core/types/app-events";
import {Router} from "@angular/router";
import {CognitoUserGroups} from "../core/types/cognito-user-groups";
import {AlertLevel} from "../core/types/alert-level";
import {PassoAutenticacaoEnum} from "../core/enum/passo-autenticacao-enum";
import {SistemaEnum} from "../core/enum/sistema-enum";

@Component({
  selector: "app-login",
  templateUrl: "./login.component.html",
  styleUrls: ["./login.component.scss"],
})
export class LoginComponent implements OnInit {
  // Define all the form groups
  protected readonly PassoAutenticacaoEnum = PassoAutenticacaoEnum;
  public passoAutenticacao = PassoAutenticacaoEnum.Login;
  public loginFormGroup: FormGroup;
  public verificationCodeResquestFormGroup: FormGroup;
  public mfaTokenFormGroup: FormGroup;
  public newPasswordFormGroup: FormGroup;
  public resetPasswordFormGroup: FormGroup;

  // Define variables
  public passwordsDiffer = false;

  constructor(
    private formBuilder: FormBuilder,
    private router: Router,
    private auth: AuthService,
    private alertify: AlertifyService,
    private cognito: CognitoService,
    private session: SessionService
  ) {}

  public ngOnInit(): void {
    this.setupAndInitializeFormsToDisplay();
  }

  public definePassoAutenticacao(passo: PassoAutenticacaoEnum) {
    this.passoAutenticacao = passo;
  }

  /**
   * Perform the first sign-in process on cognito. If the user don't need to send a mfa
   * token to complete the authentication flow, just sing in the user
   * and send it to the application home page after saving all
   * the session data into de session service and the local storage.
   * If the user needs to provide an MFA token to complete the flow, this method
   * also handles that and display the MFA token form to the user.
   * @author Renan Souza
   * @version 1.0.0
   */
  public async signInOnCognito(): Promise<void> {
    if (!this.isCognitoCredentialsValid()) return;
    this.displayLoadingComponentOnScreen();
    const credentials: UserCredentials = this.getCognitoCredentialsFromForm();
    try {
      this.displayLoadingComponentOnScreen();
      await this.cognito.autenticar(credentials.username, credentials.password);
      if (this.cognito.temDesafioDeSenhaProvisoria()) {
        this.hideLoadingComponentOnScreen();
        this.definePassoAutenticacao(PassoAutenticacaoEnum.NovaSenha);
        return;
      }
      if (this.cognito.temDesafioDeTokenMfa()) {
        this.hideLoadingComponentOnScreen();
        this.definePassoAutenticacao(PassoAutenticacaoEnum.MfaToken);
        return;
      }
      await this.setupCognitoAuthenticatedSession();
      EventEmitterBaseService.get(
        AppEvents.displayApplicationNavigationBars
      ).emit();
      this.hideLoadingComponentOnScreen();
      this.router.navigate(["/home"]).catch((error) => console.log(error));
    } catch (error) {
      if (error.message.includes("Password reset")) {
        this.definePassoAutenticacao(PassoAutenticacaoEnum.NovaSenha);
        return;
      }
      if (error.toString().includes("401")) {
        this.alertify.error(
          "Usuário não tem permissão para acessar esse sistema!",
          5
        );
      } else {
        this.alertify.error(error.message, 5);
      }
      console.error(error);
      return;
    } finally {
      this.hideLoadingComponentOnScreen();
    }
  }

  /**
   * This method is used in case the user needs to provide an MFA token to complete
   * the authentication flow. This method validates the token and send it to the
   * cognito service to complete the authentication flow.
   * @author Renan Souza
   * @version 1.0.0
   */
  public async sendMfaTokenAndFinishLogin(): Promise<void> {
    try {
      if (!this.isMfaTokenValid()) return;
      this.displayLoadingComponentOnScreen();
      const mfaToken: string = this.getCognitoMfaTokenFromForm();
      await this.cognito.enviarTokenMfa(mfaToken);
      await this.setupCognitoAuthenticatedSession();
      EventEmitterBaseService.get(
        AppEvents.displayApplicationNavigationBars
      ).emit();
      this.hideLoadingComponentOnScreen();
      await this.cognito.obterRefreshToken();
      this.router.navigate(["/home"]).catch((error) => console.log(error));
    } catch (error) {
      this.hideLoadingComponentOnScreen();
      this.session.logout();
      await this.cognito.deslogar();
      if (error.toString().includes("401")) {
        this.alertify.error(
          "Usuário não tem permissão para acessar esse sistema!",
          5
        );
      } else {
        this.alertify.error(error.message, 5);
      }
      console.error(error);
      return;
    }
  }

  /**
   * Hide all the active forms and display the login form again, resetting all the process
   * @author Renan Souza
   * @version 1.0.0
   */
  public resetProcess() {
    this.cleanAllForms();
    this.passoAutenticacao = PassoAutenticacaoEnum.Login;
  }

  /** check if the passwords informed are the same */
  public passwordMatch() {
    if (
      this.newPasswordFormGroup.controls.newPassword.value !==
      this.newPasswordFormGroup.controls.newPasswordConfirmation.value
    ) {
      this.passwordsDiffer = true;
    } else {
      this.passwordsDiffer = false;
    }
  }
  public async EnviarCodigoVerificacao() {
    this.displayLoadingComponentOnScreen();
    try {
      await this.cognito.enviarCodigoParaResetDeSenha(
        this.verificationCodeResquestFormGroup.get("email").value
      );
      this.definePassoAutenticacao(PassoAutenticacaoEnum.ResetSenha);
    } catch (error) {
      console.log(error);
    } finally {
      this.hideLoadingComponentOnScreen();
    }
  }

  public async resetarSenha() {
    this.displayLoadingComponentOnScreen();
    try {
      await this.cognito.realizarResetDeSenha(
        this.verificationCodeResquestFormGroup.get("email").value,
        this.resetPasswordFormGroup.get("verificationCode").value,
        this.resetPasswordFormGroup.get("newPassword").value
      );
      this.resetProcess();
      this.alertify.success("Senha redefinida com sucesso!", 8);
    } catch (error) {
      console.log(error);
      this.alertify.error("Não foi possível redefinir a senha.", 8);
    } finally {
      this.hideLoadingComponentOnScreen();
    }
  }

  public async completarDesafioNovaSenha() {
    this.displayLoadingComponentOnScreen();
    try {
      await this.cognito.completarEnvioDeNovaSenha(
        this.newPasswordFormGroup.get("newPassword").value
      );
      this.resetProcess();
      this.alertify.success("Senha definida com sucesso!", 8);
    } catch (error) {
      console.log(error);

      const errorMessage = error.message;

      if (errorMessage && errorMessage.includes("customChallenge")) {
        await this.cognito.deslogar();
        this.alertify.success("Nova senha criada com sucesso! Você ja pode realizar sua autenticação normalmente");
        this.definePassoAutenticacao(PassoAutenticacaoEnum.Login);
        return;
      }

      this.alertify.error("Não foi possível definir a senha.", 8);
    } finally {
      this.hideLoadingComponentOnScreen();
    }
  }
  /**
   * Gets the cognito user data and set up all the retrieved data into the session
   * @private
   * @author Renan Souza
   * @version 1.0.0
   */
  private async setupCognitoAuthenticatedSession(): Promise<void> {
    const congnitoId: string = await this.cognito.obterIdCognito();
    const userPermissions: UsuarioPerfilResponse =
      await this.session.getUserPermissionsFromDatabase(
        congnitoId,
        SistemaEnum.Bilhetagem
      );
    this.session.setUserSession(userPermissions);
  }

  private checkToSeeIfUserHasPermissionToAccessThisService(): boolean {
    if (
      !this.cognito.usuarioMembroDeUmGrupoEspecifico(
        CognitoUserGroups.Bilhetagem
      )
    ) {
      this.hideLoadingComponentOnScreen();
      this.showAlertMessage(
        "Você não tem permissão para acessar esse serviço. Procure um administrador",
        AlertLevel.Error
      );
      return false;
    }
    return true;
  }

  /**
   * Creates and build all the forms to be used
   * @private
   * @author Renan Souza
   * @version 1.0.0
   */
  private setupAndInitializeFormsToDisplay() {
    this.createLoginForm();
    this.createRetrievePasswordForm();
    this.createMfaTokenForm();
    this.createNewPasswordForm();
    this.resetPasswordForm();
  }

  /**
   * Creates the login form
   * @private
   * @author Renan Souza
   * @version 1.0.0
   */
  private createLoginForm(): void {
    this.loginFormGroup = this.formBuilder.group({
      username: ["", Validators.required],
      password: ["", Validators.required],
      context: ["bilhetagem", Validators.required],
    });
  }

  /**
   * Creates the retrieve password form
   * @private
   * @author Renan Souza
   * @version 1.0.0
   */
  private createRetrievePasswordForm(): void {
    const emailPattern = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    this.verificationCodeResquestFormGroup = this.formBuilder.group({
      email: ["", [Validators.required, Validators.pattern(emailPattern)]],
    });
  }

  /**
   * Creates the MFA token form
   * @private
   * @author Renan Souza
   * @version 1.0.0
   */
  private createMfaTokenForm(): void {
    this.mfaTokenFormGroup = this.formBuilder.group({
      mfaToken: ["", Validators.required],
    });
  }

  /**
   * Creates the new password form
   * @private
   * @author Renan Souza
   * @version 1.0.0
   */
  private createNewPasswordForm(): void {
    const passwordPattern = /^(?=.*[0-9])(?=.*[A-Z])(?=.*[a-z]).{6,}$/;
    this.newPasswordFormGroup = this.formBuilder.group({
      newPassword: [
        "",
        [Validators.required, Validators.pattern(passwordPattern)],
      ],
      newPasswordConfirmation: [
        "",
        [Validators.required, Validators.pattern(passwordPattern)],
      ],
    });
  }

  private resetPasswordForm(): void {
    const passwordPattern = /^(?=.*[0-9])(?=.*[A-Z])(?=.*[a-z]).{6,}$/;
    this.resetPasswordFormGroup = this.formBuilder.group({
      newPassword: [
        "",
        [Validators.required, Validators.pattern(passwordPattern)],
      ],
      verificationCode: ["", [Validators.required]],
    });
  }

  /**
   * Validates if the user provided all the credentials to perform login
   * @private
   * @author Renan Souza
   * @version 1.0.0
   */
  private isCognitoCredentialsValid(): boolean {
    if (
      !this.loginFormGroup.get("username").valid ||
      !this.loginFormGroup.get("password").valid
    ) {
      this.alertify.error("Login e/ou senha inválido", 5);
      return false;
    }
    return true;
  }

  /**
   * Retrieves all the data from the credentials form
   * @private
   * @author Renan Souza
   * @version 1.0.0
   */
  private getCognitoCredentialsFromForm(): UserCredentials {
    return {
      username: this.loginFormGroup.get("username").value,
      password: this.loginFormGroup.get("password").value,
    } as UserCredentials;
  }

  /**
   * Retrieves the mfa token from the form
   * @private
   * @author Renan Souza
   * @version 1.0.0
   */
  private getCognitoMfaTokenFromForm(): string {
    return this.mfaTokenFormGroup.get("mfaToken").value;
  }

  /**
   * Validates the user mfa token
   * @private
   * @author Renan Souza
   * @version 1.0.0
   */
  private isMfaTokenValid(): boolean {
    if (!this.mfaTokenFormGroup.get("mfaToken").valid) {
      this.alertify.error("Token de acesso não fornecido", 5);
      return false;
    }
    return true;
  }

  /**
   * Display the loading component on the screen
   * @private
   * @author Renan Souza
   * @version 1.0.0
   */
  private displayLoadingComponentOnScreen(): void {
    this.auth.showLoadingEmitter.emit(true);
  }

  /**
   * Hide the loading component if it's active on screen
   * @private
   * @author Renan Souza
   * @version 1.0.0
   */
  private hideLoadingComponentOnScreen(): void {
    this.auth.showLoadingEmitter.emit(false);
  }

  /**
   * Reset all the form groups to the default value.
   * @private
   * @author Renan Souza
   * @version 1.0.0
   */
  private cleanAllForms(): void {
    this.loginFormGroup.reset();
    this.mfaTokenFormGroup.reset();
    this.verificationCodeResquestFormGroup.reset();
  }

  /**
   * Display an alert message to the usert
   * @param message The message to display
   * @param type The level of the alert
   * @param duration the duration in seconds of the alert
   * @private
   * @author Renan Souza
   * @version 1.0.0
   */
  private showAlertMessage(
    message: string,
    type: string,
    duration: number = 5
  ): void {
    this.alertify[type](message, duration);
  }
}
